import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { PatientConsumerRenderProps, withPatientContext } from 'components/patient/PatientContext';
import CostEstimationDropdown from 'components/treatments/segments/CostEstimationDropdown';
import { EditTreatmentValues } from 'components/treatments/TreatmentsContext';
import React from 'react';
import { Field } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Grid, Label } from 'semantic-ui-react';
import styled from 'styled-components';
import { ConsultationDto, CostEstimationDto } from 'ts-types/api.types';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import { composeValidators, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';

const TreatmentInfoGrid = styled(PraxFormGrid)`
  .treatment-tags .ui.checkbox:not(:first-child) {
    margin-left: 0.675rem;
  }
`;

interface Props extends PatientConsumerRenderProps, MessageSourceProps {
  tariffs: Array<DropdownOption>,
  reasons: Array<DropdownOption>,
  costCenters: Array<DropdownOption>,
  costEstimations: Array<CostEstimationDto>,
  tariffId: number,
  patientId: number,
  treatment: EditTreatmentValues,
  treatmentDate: string,
  readOnly: boolean,
  currentConsultations: Array<ConsultationDto>,
  isPatientUnderaged: boolean
}

const TreatmentInfoSegment = (props: Props) => {

  const {
    tariffs, reasons, costCenters, costEstimations, currentPatient,
    getMessage, readOnly, tariffId, patientId, treatment, currentConsultations, isPatientUnderaged
  } = props;

  let firstConsultationDate = currentConsultations
      .map(c => c.consultationDate)
      .reduce((earliestCd, cd) => {
        return cd < earliestCd ? cd : earliestCd;
      }, "");

  const disabledBefore = currentPatient && new Date(currentPatient.birthDate);
  const disabledAfter = currentConsultations.length && new Date(firstConsultationDate);

  let treatmentHeader = getMessage('treatment');
  if (treatment && treatment.id !== -1) {
    treatmentHeader = getMessage('treatment') + ": " + treatment.id;
  }

  const isTreatmentDeleted = treatment && treatment.deleted === 1;

  return (
      <TreatmentInfoGrid>
        <Grid.Row>
          <Grid.Column width={3} verticalAlign="middle">
            <h4>{treatmentHeader}</h4>
          </Grid.Column>
          {isTreatmentDeleted &&
              <Grid.Column width={5} verticalAlign="middle">
                <Label color="red" tag>
                  {getMessage("treatment.deletedTreatment.label")}
                </Label>
              </Grid.Column>
          }
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            {getMessage('date')}
          </Grid.Column>
          <Grid.Column width={4}>
            <Field
                name="treatmentDate"
                component={DatePicker}
                disabledBefore={disabledBefore}
                disabledAfter={disabledAfter}
                disabled={readOnly}
            />
          </Grid.Column>
          <Grid.Column width={1}></Grid.Column>
          <Grid.Column width={3}>
            {getMessage('tariff')}
          </Grid.Column>
          <Grid.Column width={5}>
            <Field
                name="tariffId"
                component={Select}
                placeholder={`${getMessage('select')} ${getMessage('tariff')}`}
                options={tariffs}
                disabled={readOnly}
                fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            {getMessage('treatmentForm.accidentDate')}
          </Grid.Column>
          <Grid.Column width={4}>
            <Field
                name="accidentDate"
                component={DatePicker}
                disabledBefore={disabledBefore}
                disabled={readOnly}
            />
          </Grid.Column>
          <Grid.Column width={1}></Grid.Column>
          <Grid.Column width={3}>
            {getMessage('treatment.info.reason')}
          </Grid.Column>
          <Grid.Column width={5}>
            <Field
                name="reasonId"
                component={Select}
                placeholder={`${getMessage('select')} ${getMessage('treatment.info.reason')}`}
                options={reasons}
                disabled={readOnly}
                fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
          </Grid.Column>
          <Grid.Column className="treatment-tags" width={5}>
            <Field
                name="isTreatmentInterrupted"
                component={CheckBox}
                label={getMessage('treatmentForm.interrupted')}
                disabled={readOnly}
            />
            <Field
                name="status"
                component={CheckBox}
                label={getMessage('treatmentForm.closed')}
                disabled={readOnly}
            />
          </Grid.Column>
          <Grid.Column width={3}>
            {getMessage('treatmentForm.billPeriod')}
          </Grid.Column>
          <Grid.Column width={5}>
            <Field
                name="billPeriod"
                component={Input}
                onKeyDown={ensureOnlyNumericInput}
                validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
                disabled={readOnly}
                fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8}>
          </Grid.Column>
          <Grid.Column width={3}>
            {getMessage('treatmentForm.costCenter')}
          </Grid.Column>
          <Grid.Column width={5}>
            <Field
                name="costCenterId"
                component={Select}
                placeholder={`${getMessage('select')} ${getMessage('treatmentForm.costCenter')}`}
                options={costCenters}
                disabled={readOnly}
                fluid
            />
          </Grid.Column>
        </Grid.Row>
        <CostEstimationDropdown
            costEstimations={costEstimations}
            tariffs={tariffs}
            tariffId={tariffId}
            readOnly={readOnly}
            patientId={patientId}
            treatment={treatment}
        />
        {isPatientUnderaged &&
            <Grid.Row>
              <Grid.Column width={8}>
              </Grid.Column>
              <Grid.Column width={3}>
                {getMessage("treatmentForm.parent")}
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="parent"
                    component={Input}
                    fluid
                    validate={required}
                    disabled={readOnly}
                />
              </Grid.Column>
            </Grid.Row>

        }
      </TreatmentInfoGrid>
  );
};

export default withPatientContext(withMessages(TreatmentInfoSegment));
